import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import { Calendar, ArrowRight } from 'lucide-react';
import leaveService, { type LeaveBalance, type LeaveBalanceSource } from '../services/leave';
import { useOrgTimezone, formatDateTimeInTz } from '../utils/timezone';

function leaveTypeLabel(type: string): string {
  const t = type.trim().toLowerCase();
  if (t === 'time_in_lieu') return 'Time in Lieu';
  if (t === 'personal') return 'Personal / Sick';
  if (t === 'annual') return 'Annual Leave';
  if (t === 'long_service') return 'Long Service Leave';
  const words = t.replace(/_/g, ' ');
  const titled = words.replace(/\b\w/g, c => c.toUpperCase());
  return titled.toLowerCase().endsWith('leave') ? titled : titled + ' Leave';
}

/**
 * Dashboard card showing a compact snapshot of all leave balances.
 * Renders nothing while loading with no data, or if no balances exist.
 */
export function LeaveBalancesCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [source, setSource] = useState<LeaveBalanceSource | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setBalances([]);
    leaveService.getBalances()
      .then(res => {
        if (res.success && res.data) setBalances(res.data);
        setSource(res.meta ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.organisation_id]);

  if (!loading && balances.length === 0) return null;

  return (
    <Card
      className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
      onClick={() => navigate('/timesheet')}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg text-neutral-600">Leave Balances</CardTitle>
        <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
          <Calendar className="h-6 w-6 text-green-600" />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-4 bg-neutral-100 rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {balances.map(b => (
                <div key={b.id} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-600">{leaveTypeLabel(b.leave_type)}</span>
                  <span className="font-semibold text-neutral-800">
                    {Number(b.available_hours).toFixed(1)}h
                  </span>
                </div>
              ))}
            </div>
            {source?.source === 'xero' && source.owned_types.length > 0 && (
              <p className="mt-2 text-xs text-neutral-500">
                From Xero, as at the last pay run
                {source.synced_at ? ` · read ${formatDateTimeInTz(source.synced_at, orgTz)}` : ''}
              </p>
            )}
            <div className="mt-3 flex items-center gap-1 text-xs text-green-600 font-medium">
              View details <ArrowRight className="h-3 w-3" />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default LeaveBalancesCard;
